import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Upload, FileSpreadsheet, CheckCircle2, AlertCircle, Loader2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import Papa from 'papaparse';
import { 
  importUsers, 
  importContacts, 
  importDeals, 
  importItems, 
  importPayments, 
  importLeads, 
  importCalls, 
  importNotes,
  importContactNotes,
  importDealNotes
} from '@/utils/zohoImport';
import { supabase } from '@/integrations/supabase/client';
import { safeStorageJson } from '@/lib/safeJson';

const STEPS = [
  { id: 'users', label: 'Users (Usuários)', importFn: importUsers },
  { id: 'contacts', label: 'Contacts (Contatos)', importFn: importContacts },
  { id: 'deals', label: 'Deals (Negócios)', importFn: importDeals },
  { id: 'items', label: 'Deal Items (Itens)', importFn: importItems },
  { id: 'payments', label: 'Payments (Pagamentos)', importFn: importPayments },
  { id: 'notes', label: 'Notes - Items (Notas de Itens)', importFn: importNotes, noFile: true },
  { id: 'contact_notes', label: 'Contact Notes (Notas de Contatos)', importFn: importContactNotes },
  { id: 'deal_notes', label: 'Deal Notes (Notas de Deals)', importFn: importDealNotes },
  { id: 'leads', label: 'Leads', importFn: importLeads },
  { id: 'calls', label: 'Calls (Chamadas)', importFn: importCalls },
];

const ZohoImport = () => {
  const [currentStep, setCurrentStep] = useState(() => {
    const saved = localStorage.getItem('zoho_import_step');
    return saved ? parseInt(saved) : 0;
  });
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [stepResults, setStepResults] = useState(
    () => safeStorageJson('zoho_import_results', {})
  );
  const [realtimeCount, setRealtimeCount] = useState(0);
  const [importStartTime, setImportStartTime] = useState(null);
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0 });

  const currentStepData = STEPS[currentStep];
  const currentResult = stepResults[currentStepData.id];
  const progress = ((currentStep + (currentResult ? 1 : 0)) / STEPS.length) * 100;

  // Real-time updates durante importação
  useEffect(() => {
    if (!importing || !importStartTime) return;

    const channel = supabase
      .channel('import-updates')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: currentStepData.id === 'deals' ? 'deals' : 
                 currentStepData.id === 'contacts' ? 'contacts' :
                 currentStepData.id === 'leads' ? 'leads' :
                 currentStepData.id === 'calls' ? 'calls' :
                 currentStepData.id === 'items' ? 'items' : 'app_users'
        },
        (payload) => {
          const recordTime = new Date(payload.new.created_at).getTime();
          if (recordTime >= importStartTime) {
            setRealtimeCount(prev => prev + 1);
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: currentStepData.id === 'deals' ? 'deals' : 
                 currentStepData.id === 'contacts' ? 'contacts' :
                 currentStepData.id === 'leads' ? 'leads' :
                 currentStepData.id === 'calls' ? 'calls' :
                 currentStepData.id === 'items' ? 'items' : 'app_users'
        },
        (payload) => {
          const recordTime = new Date(payload.new.updated_at).getTime();
          if (recordTime >= importStartTime) {
            setRealtimeCount(prev => prev + 1);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [importing, importStartTime, currentStepData.id]);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      toast.success(`${selectedFile.name} carregado`);
    }
  };

  const parseCSV = (file) => {
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => resolve(results.data),
        error: (error) => reject(error),
      });
    });
  };

  const handleImportStep = async () => {
    const step = STEPS[currentStep];

    if (!file && !step.noFile) {
      toast.error('Por favor, carregue um arquivo CSV');
      return;
    }

    setImporting(true);
    setRealtimeCount(0);
    setBatchProgress({ current: 0, total: 0 });
    setImportStartTime(Date.now());

    try {
      let data = [];
      if (!step.noFile && file) {
        toast.info(`Parseando ${step.label}...`);
        data = await parseCSV(file);
      }

      toast.info(`Importando ${step.label}...`);
      
      // Callback de progresso para contact_notes e deal_notes
      const progressCallback = (current, total) => {
        setBatchProgress({ current, total });
      };
      
      const result = step.id === 'contact_notes' || step.id === 'deal_notes'
        ? await step.importFn(data, progressCallback)
        : await step.importFn(data);

      const newResults = { ...stepResults, [step.id]: result };
      setStepResults(newResults);
      localStorage.setItem('zoho_import_results', JSON.stringify(newResults));
      toast.success(`${step.label} importado com sucesso!`);
      
      // Auto-avançar para próximo passo se não for o último
      if (currentStep < STEPS.length - 1) {
        setTimeout(() => {
          const nextStep = currentStep + 1;
          setCurrentStep(nextStep);
          localStorage.setItem('zoho_import_step', nextStep.toString());
          setFile(null);
        }, 1000);
      }
    } catch (error) {
      console.error('Erro na importação:', error);
      toast.error(`Erro: ${error.message}`);
    } finally {
      setImporting(false);
      setImportStartTime(null);
      setRealtimeCount(0);
      setBatchProgress({ current: 0, total: 0 });
    }
  };

  const handleSkipStep = () => {
    const nextStep = currentStep + 1;
    setCurrentStep(nextStep);
    localStorage.setItem('zoho_import_step', nextStep.toString());
    setFile(null);
    toast.info(`Etapa "${currentStepData.label}" pulada`);
  };

  const handleResetImport = () => {
    if (confirm('Tem certeza que deseja resetar toda a importação? Isso não apaga os dados já importados.')) {
      setCurrentStep(0);
      setStepResults({});
      setFile(null);
      localStorage.removeItem('zoho_import_step');
      localStorage.removeItem('zoho_import_results');
      toast.success('Importação resetada');
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <div className="mb-8 flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold mb-2">Importação Zoho CRM</h1>
          <p className="text-muted-foreground">
            Importe os arquivos CSV do Zoho um de cada vez, seguindo a ordem
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={handleResetImport}>
          Resetar Importação
        </Button>
      </div>

      {/* Progress Bar */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>Progresso Total</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>
          <div className="flex gap-2 mt-4">
            {STEPS.map((step, idx) => (
              <div
                key={step.id}
                className={`flex-1 h-2 rounded-full transition-colors ${
                  stepResults[step.id]
                    ? 'bg-green-500'
                    : idx === currentStep
                    ? 'bg-primary'
                    : 'bg-muted'
                }`}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Current Step */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>
            Passo {currentStep + 1} de {STEPS.length}: {currentStepData.label}
          </CardTitle>
          <CardDescription>
            {currentStep === 0 && 'Comece importando os usuários do sistema'}
            {currentStep === 1 && 'Agora importe os contatos (precisa dos usuários)'}
            {currentStep === 2 && 'Importe os deals/negócios (precisa dos contatos)'}
            {currentStep === 3 && 'Importe os itens dos deals (precisa dos deals)'}
            {currentStep === 4 && 'Importe os planos de pagamento (precisa dos deals)'}
            {currentStep === 5 && 'Gerar/atualizar notas dos deals apenas com itens (não precisa de arquivo)'}
            {currentStep === 6 && 'Importe as notas dos contatos (9,347 registros)'}
            {currentStep === 7 && 'Importe as notas dos deals/negócios (2,299 registros)'}
            {currentStep === 8 && 'Importe os leads'}
            {currentStep === 9 && 'Importe as chamadas'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!currentResult ? (
            <>
              {!currentStepData?.noFile && (
                <label className="flex items-center gap-2 p-4 border-2 border-dashed rounded-lg cursor-pointer hover:border-primary transition-colors">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    className="hidden"
                    disabled={importing}
                  />
                  {file ? (
                    <>
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                      <span className="font-medium">{file.name}</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5" />
                      <span>Carregar {currentStepData.label}</span>
                    </>
                  )}
                </label>
              )}

              {importing && batchProgress.total > 0 && (
                <Alert className="mb-4">
                  <AlertDescription>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">Processando em lotes...</span>
                      <span className="text-lg font-bold text-primary">
                        {batchProgress.current} / {batchProgress.total}
                      </span>
                    </div>
                    <Progress 
                      value={(batchProgress.current / batchProgress.total) * 100} 
                      className="h-2"
                    />
                    <div className="text-xs text-muted-foreground mt-1">
                      {Math.round((batchProgress.current / batchProgress.total) * 100)}% completo
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              {importing && realtimeCount > 0 && batchProgress.total === 0 && (
                <Alert className="mb-4">
                  <AlertDescription className="flex items-center justify-between">
                    <span className="font-medium">Processando em tempo real...</span>
                    <span className="text-lg font-bold text-primary">{realtimeCount} registros</span>
                  </AlertDescription>
                </Alert>
              )}

              <div className="flex gap-2">
                <Button
                  onClick={handleImportStep}
                  disabled={importing || (!file && !currentStepData?.noFile)}
                  className="flex-1"
                  size="lg"
                >
                  {importing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Importando {currentStepData.label}...
                    </>
                  ) : (
                    <>
                      <FileSpreadsheet className="mr-2 h-4 w-4" />
                      Importar {currentStepData.label}
                    </>
                  )}
                </Button>
                {currentStep < STEPS.length - 1 && (
                  <Button
                    onClick={handleSkipStep}
                    disabled={importing}
                    variant="outline"
                    size="lg"
                  >
                    Pular
                  </Button>
                )}
              </div>
            </>
          ) : (
            <>
              {/* Results */}
              <div className="border-l-4 border-green-500 pl-4 bg-green-50 dark:bg-green-950 p-4 rounded">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <h3 className="font-semibold">Importação Concluída</h3>
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm mb-2">
                  <div>
                    <span className="text-muted-foreground">Criados:</span>{' '}
                    <span className="font-semibold text-green-600">{currentResult.created}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Atualizados:</span>{' '}
                    <span className="font-semibold text-blue-600">{currentResult.updated}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Ignorados:</span>{' '}
                    <span className="font-semibold text-gray-600">{currentResult.skipped}</span>
                  </div>
                </div>
                {currentResult.notesCreated > 0 && (
                  <div className="text-sm">
                    <span className="text-muted-foreground">Notas criadas:</span>{' '}
                    <span className="font-semibold">{currentResult.notesCreated}</span>
                  </div>
                )}
                {currentResult.errors && currentResult.errors.length > 0 && (
                  <Alert className="mt-3">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      <p className="font-semibold mb-1">{currentResult.errors.length} erros:</p>
                      <ul className="list-disc list-inside text-xs max-h-32 overflow-y-auto">
                        {currentResult.errors.slice(0, 5).map((err, i) => (
                          <li key={i}>{err}</li>
                        ))}
                        {currentResult.errors.length > 5 && (
                          <li className="font-semibold">
                            ... e mais {currentResult.errors.length - 5} erros
                          </li>
                        )}
                      </ul>
                    </AlertDescription>
                  </Alert>
                )}
              </div>

              {currentStep < STEPS.length - 1 && (
                <Button
                  onClick={() => {
                    const nextStep = currentStep + 1;
                    setCurrentStep(nextStep);
                    localStorage.setItem('zoho_import_step', nextStep.toString());
                    setFile(null);
                  }}
                  className="w-full"
                  size="lg"
                >
                  Próximo Passo
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              )}

              {currentStep === STEPS.length - 1 && (
                <Alert>
                  <CheckCircle2 className="h-4 w-4" />
                  <AlertDescription>
                    <p className="font-semibold">🎉 Importação Completa!</p>
                    <p className="text-sm mt-1">
                      Todos os dados foram importados. As etapas concluídas não podem ser reimportadas.
                    </p>
                  </AlertDescription>
                </Alert>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Summary of completed steps */}
      {Object.keys(stepResults).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Resumo das Etapas Concluídas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {STEPS.filter(step => stepResults[step.id]).map((step) => {
              const result = stepResults[step.id];
              return (
                <div key={step.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span className="font-medium">{step.label}</span>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {result.created + result.updated} registros
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ZohoImport;
